import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { Row } from "../../components/Row";
import { Check } from "../../components/Check/Check";
import { DataTable } from "../../components/DataTable/DataTable";
import { VCStatsDisplay } from "./VCStatsDisplay";
import { VerseCardsGame } from "./VerseCardsGame";
import { useRequestHandler } from "../../hooks/requestHandler";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { shuffle } from "../../util/generalUtil";
import { useAppSelector } from "../../state/store";
import { VerseType } from "../../types/practiceTypes";
import { addReviewRun } from "../../state/actions/review";
import {
  constructReviewRun,
  TotalVerseCardsStats,
  verseCardsStateKey,
  VerseStats,
} from "./VerseCardsData";
import { v4 as uuid } from "uuid";
import jsQR from "jsqr";
import { ReactComponent as ReviewIcon } from "../../icons/mmButtonIcons/review-stats.svg";
import { ReactComponent as VerseCardsIcon } from "../../icons/mmButtonIcons/cards.svg";
import "./VerseCards.scss";
import { Grid } from "@mui/material";

export function VerseCards() {
  useDocumentTitle("Verse Cards - Bible Quiz Academy");
  const location = useLocation();
  const handler = useRequestHandler({
    loading: (state) => state.review.loading,
    onSuccess: (key) => {
      if (key === "SAVE") {
        localStorage.removeItem(verseCardsStateKey);
        setRunSaved(true);
      }
    },
  });
  const material = useAppSelector((state) => state.material.material);
  const settings = useAppSelector((state) => state.settings);
  const { user } = useAppSelector((state) => state.authentication);

  // This run
  const chapters =
    location.state?.verseCards?.material || settings.selectedChapters;
  const [randomized, setRandomized] = useState<VerseType[]>([]);
  const [bottomOfDeck, setBottomOfDeck] = useState<VerseType[]>([]);
  const [runId, setRunId] = useState(uuid());
  const [stats, setStats] = useState<TotalVerseCardsStats>({
    totalVerses: 0,
    correct: 0,
    incorrect: 0,
    time: 0,
    absCorrect: 0,
    absIncorrect: 0,
    absTime: 0,
  });
  const [verseStats, setVerseStats] = useState<VerseStats[]>([]);
  const [runSaved, setRunSaved] = useState(false);

  // Settings
  const [vcMode, setVcMode] = useState<"QUIZZER" | "COACH">("QUIZZER");

  // At the end
  const [phase, setPhase] = useState<"BEFORE" | "GAME" | "END">("BEFORE");
  const [verseStatsTab, setVerseStatsTab] = useState<"VERSE" | "CHAPTER">(
    "VERSE"
  );
  const chapterStats = useMemo(() => {
    if (verseStatsTab !== "CHAPTER") return [];
    const result: Record<string, VerseStats & { total: number }> = {};
    verseStats?.forEach((verse) => {
      const key = `${verse.b}-${verse.c}`;
      if (!result[key])
        result[key] = { ...verse, total: 0, correct: 0, incorrect: 0, time: 0 };
      result[key].total += 1;
      result[key].correct += verse.correct;
      result[key].incorrect += verse.incorrect;
      result[key].time += verse.time;
    });
    return Object.values(result);
  }, [phase, verseStats, verseStatsTab]);

  // Get verse array, possibly randomized
  useEffect(() => {
    if (material.length === 0) return;
    const allVerses: VerseType[] = [];
    material.forEach((book, index) => {
      book.chapters.forEach((chapter, chapterIndex) => {
        if (chapters && chapters[index] && chapters[index][chapterIndex]) {
          chapter.forEach((verse, verseIndex) => {
            const chapterFromSettings = chapters[index][chapterIndex];
            const chapterSettings =
              typeof chapterFromSettings === "boolean"
                ? Array(chapter.length).fill(chapterFromSettings)
                : chapterFromSettings;
            if (chapterSettings[verseIndex]) {
              if (
                settings.qsInVerseFilter !== undefined &&
                verse.questions.length !== settings.qsInVerseFilter
              )
                return;
              allVerses.push({
                book: index,
                chapter: chapterIndex,
                verseNum: verseIndex,
                reference: `${book.bookName} ${chapterIndex + 1}:${
                  verseIndex + 1
                }`,
                ...verse,
              });
            }
          });
        }
      });
    });
    if (settings.vcRandomize === "RANDOM") {
      shuffle(allVerses);
    } else if (settings.vcRandomize === "REVERSE") {
      allVerses.reverse();
    }
    setRandomized(allVerses);
    setStats({
      ...stats,
      totalVerses: allVerses.length,
    });
  }, [material]);

  // Importing and exporting
  const [showQRImport, setShowQRImport] = useState(false);
  const videoStream = useRef<MediaStream | null>(null);
  const importFromQR = (code: string, displayError = false) => {
    try {
      const data = JSON.parse(code);
      setStats(data.s);
      const parseList = (key: "v" | "d") => {
        const list: VerseType[] = [];
        data[key]?.forEach((item: string) => {
          // book-chapter-verse
          const parts = item.split("-");
          const book = Number.parseInt(parts[0]);
          const chapter = Number.parseInt(parts[1]);
          const verseNum = Number.parseInt(parts[2]);
          const verse = material[book].chapters[chapter][verseNum];
          list.push({
            book,
            chapter,
            verseNum,
            reference: `${material[book].bookName} ${chapter + 1}:${
              verseNum + 1
            }`,
            verse: verse.verse,
            questions: verse.questions,
          });
        });
        return list;
      };
      const newRandomized: VerseType[] = parseList("v");
      const newDeck: VerseType[] = parseList("d");

      // Set state variables and finish up
      setRandomized(newRandomized);
      setBottomOfDeck(newDeck);
      setShowQRImport(false);
      videoStream.current?.getTracks()?.[0]?.stop();
      setPhase("GAME");
      return true;
    } catch {
      // Data invalid for any reason
      if (displayError) {
        handler.showMessage("The QR Code is an invalid format.", "error");
      }
      return false;
    }
  };

  useEffect(() => {
    if (!showQRImport) return;
    // Get video and canvas elements
    const video = document.createElement("video");
    const canvasElement = document.getElementById(
      "canvas"
    ) as HTMLCanvasElement | null;
    if (!video || !canvasElement) return;
    const canvas = canvasElement.getContext("2d");
    if (!canvas) return;
    if (!navigator.mediaDevices) {
      handler.showMessage(
        "QR scanning is not available on this device, but you can upload a screenshot of a QR code.",
        "error"
      );
      return;
    }
    navigator.mediaDevices.getUserMedia({ video }).then((stream) => {
      // Put the stream on the video element
      video.srcObject = stream;
      videoStream.current = stream;
      // @ts-ignore
      video.setAttribute("playsinline", true); // required to tell iOS safari we don't want fullscreen
      video.play();
      let interval: NodeJS.Timer | null = null;

      const tick = () => {
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          // Paint the canvas with the image from the stream
          canvasElement.hidden = false;
          canvasElement.height = video.videoHeight;
          canvasElement.width = video.videoWidth;
          canvas.drawImage(
            video,
            0,
            0,
            canvasElement.width,
            canvasElement.height
          );

          // Get the image
          const imageData = canvas.getImageData(
            0,
            0,
            canvasElement.width,
            canvasElement.height
          );
          // Attempt to decode the qr
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "dontInvert",
          });
          if (code) {
            if (importFromQR(code.data)) {
              // Import successful
              handler.showMessage("Verses imported!", "success");
              if (interval) clearInterval(interval);
            }
          }
        }
      };

      // Check for a qr code every 50 ms
      interval = setInterval(() => {
        requestAnimationFrame(tick);
      }, 50);
    });
  }, [showQRImport]);

  // Restore and save of previously finished verse cards
  const [canRestore, setCanRestore] = useState(false);
  useEffect(() => {
    const previousVC = localStorage.getItem(verseCardsStateKey);
    if (previousVC) {
      const verseCards = JSON.parse(previousVC);
      if (verseCards.versesRemaining.length === 0) {
        const newRun = constructReviewRun(
          material,
          verseCards.verseStats,
          verseCards.runId,
          verseCards.time
        );
        handler.runRequest(addReviewRun(newRun), "", "", "SAVE");
      } else {
        setCanRestore(true);
      }
    }
  }, []);

  if (!chapters || chapters.length === 0) {
    return <Navigate to="/" />;
  }

  const getMainContent = () => {
    if (phase === "BEFORE") {
      return (
        <>
          {canRestore && (
            <button
              className="link"
              onClick={() => {
                const previousState = JSON.parse(
                  localStorage.getItem(verseCardsStateKey) as string
                );
                setRandomized(previousState.versesRemaining);
                setBottomOfDeck(previousState.deck);
                setStats(previousState.stats);
                setVerseStats(previousState.verseStats || []);
                setRunId(previousState.runId || uuid());
                setPhase("GAME");
              }}
            >
              Restore Previous State
            </button>
          )}
          <br />
          <br />
          {!showQRImport && (
            <>
              <button className="link" onClick={() => setShowQRImport(true)}>
                QR Code Import
              </button>
              <br />
              <br />
              <p>{`Total Verses Selected: ${stats.totalVerses}`}</p>
              <h3>Select a Verse Cards Mode:</h3>
              <Grid container spacing={2} style={{ maxWidth: 550 }}>
                <Grid size={6}>
                  <Check
                    checked={vcMode === "QUIZZER"}
                    onClick={() => setVcMode("QUIZZER")}
                  >
                    Quizzer Mode
                  </Check>
                  <p>
                    Standard Verse Cards - you quote, and check yourself at the
                    end of each verse.
                  </p>
                </Grid>
                <Grid size={6}>
                  <Check
                    checked={vcMode === "COACH"}
                    onClick={() => setVcMode("COACH")}
                  >
                    Coach Mode
                  </Check>
                  <p>
                    Designed for you to listen to a quizzer quote - you see the
                    verse immediately, and time runs until you click stop or
                    make a ruling.
                  </p>
                </Grid>
              </Grid>
              {stats.totalVerses > 0 && (
                <>
                  <button
                    className="large-button"
                    onClick={() => setPhase("GAME")}
                    data-testid="vc-start"
                  >
                    <VerseCardsIcon />
                    Start!
                  </button>
                  <br />
                  <br />
                </>
              )}
              <hr style={{ marginBottom: 20 }} />
              {user ? (
                <Link to="/review" className="large-button">
                  <ReviewIcon />
                  My Stats
                </Link>
              ) : (
                <p>Log in to see personalized verse cards stats!</p>
              )}
            </>
          )}
          {showQRImport && (
            <>
              <p>
                You can upload a screenshot of a QR code by clicking the button
                below.
              </p>
              <label
                htmlFor="qr-upload"
                className="secondary-btn"
                onClick={(e) => e.stopPropagation()}
              >
                Upload QR Code Picture
              </label>
              <input
                type="file"
                id="qr-upload"
                className="secondary-btn"
                accept="image/png, image/jpeg"
                style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];

                  if (!file) return;
                  const url = URL.createObjectURL(file);

                  const img = document.createElement("img");
                  img.src = url;
                  document.body.appendChild(img);
                  // @ts-ignore
                  img.addEventListener("load", () => {
                    const canvas = document.createElement("canvas"); // Creates a canvas  object
                    canvas.width = img.naturalWidth; // Assigns image's width to canvas
                    canvas.height = img.naturalHeight; // Assigns image's height to canvas

                    const context = canvas.getContext("2d"); // Creates a contect object
                    context!.imageSmoothingEnabled = false;
                    context!.drawImage(
                      img,
                      0,
                      0,
                      img.naturalWidth,
                      img.naturalHeight
                    ); // Draws the image on canvas

                    const imageData = context!.getImageData(
                      0,
                      0,
                      img.naturalWidth,
                      img.naturalHeight
                    ); // Assigns image base64 string in jpeg format to a variable
                    const code = jsQR(
                      imageData.data,
                      img.naturalWidth,
                      img.naturalHeight
                    );
                    if (!code)
                      handler.showMessage("Unable to read QR code.", "error");
                    else {
                      importFromQR(code.data);
                    }
                  });

                  document.body.removeChild(img);
                }}
              />
              <br />
              <br />
              <p>
                Or, you can scan the QR Code with your camera (you may need to
                give BQA camera permissions).
              </p>
              <div>
                <canvas id="canvas" hidden style={{ maxHeight: 200 }}></canvas>
              </div>
              <button
                style={{ marginTop: 40 }}
                className="secondary-btn"
                onClick={() => setShowQRImport(false)}
              >
                Cancel
              </button>
            </>
          )}
        </>
      );
    }

    if (phase === "GAME") {
      return (
        <VerseCardsGame
          key="verse-cards"
          material={randomized}
          bottomOfDeck={bottomOfDeck}
          startingStats={stats}
          startingVerseStats={verseStats}
          onComplete={(stats, verses) => {
            setPhase("END");
            setStats(stats);
            setVerseStats(verses);
            if (!user) return;

            const newRun = constructReviewRun(material, verses, runId);
            handler.runRequest(addReviewRun(newRun), "", "", "SAVE");
          }}
          runId={runId}
          allowExport
          allowSkip
          handler={handler}
          mode={vcMode}
        />
      );
    }

    // End
    return (
      <>
        <p>Verses Finished!</p>
        {runSaved && (
          <div style={{ marginBottom: 20 }}>
            <Link to="/review" className="link">
              See all your verse card stats!
            </Link>
          </div>
        )}
        <VCStatsDisplay stats={stats} />
        <div style={{ marginTop: 30, maxWidth: 800 }}>
          <h2>Final Stats</h2>
          <Row style={{ marginBottom: 10 }}>
            <Check
              checked={verseStatsTab === "VERSE"}
              onClick={() => setVerseStatsTab("VERSE")}
              style={{ width: 130, marginRight: 5 }}
            >
              Every Verse
            </Check>
            <Check
              checked={verseStatsTab === "CHAPTER"}
              onClick={() => setVerseStatsTab("CHAPTER")}
              style={{ width: 210 }}
            >
              Group by Chapter
            </Check>
          </Row>
          {verseStatsTab === "VERSE" && (
            <DataTable
              data={verseStats}
              columns={[
                {
                  title: "Reference",
                  property: "verse",
                  render: (v) =>
                    `${material[v.b].bookName} ${v.c + 1}:${v.v + 1}`,
                  sortFunc: (v) => v.b * 10000 + v.c * 100 + v.v,
                  defaultSortDirection: "asc",
                },
                {
                  title: "Incorrect",
                  property: "incorrect",
                  render: (v) => v.incorrect,
                  sortFunc: true,
                  defaultSortDirection: "desc",
                },
                {
                  title: "Average Time",
                  property: "time",
                  render: (v) =>
                    (v.time / (v.incorrect + v.correct)).toFixed(1),
                  sortFunc: (v) => v.time / (v.incorrect + v.correct),
                  defaultSortDirection: "desc",
                },
              ]}
              defaultSort={{ property: "verse", direction: "asc" }}
            />
          )}
          {verseStatsTab === "CHAPTER" && (
            <DataTable
              data={chapterStats}
              columns={[
                {
                  title: "Chapter",
                  property: "chapter",
                  render: (c) => `${material[c.b].bookName} ${c.c + 1}`,
                  sortFunc: (c) => c.b * 10000 + c.c * 100,
                  defaultSortDirection: "asc",
                },
                {
                  title: "Verses",
                  property: "total",
                  render: (c) => c.total,
                  sortFunc: true,
                  defaultSortDirection: "asc",
                },
                {
                  title: "Percentage",
                  property: "percentage",
                  render: (c) =>
                    ((c.correct * 100) / (c.correct + c.incorrect)).toFixed(1) +
                    "%",
                  sortFunc: (c) => c.correct / (c.correct + c.incorrect),
                  defaultSortDirection: "asc",
                },
                {
                  title: "Total Incorrect",
                  property: "incorrect",
                  render: (c) => c.incorrect,
                  sortFunc: true,
                  defaultSortDirection: "desc",
                },
                {
                  title: "Average Time",
                  property: "time",
                  render: (c) =>
                    (c.time / (c.correct + c.incorrect)).toFixed(1),
                  sortFunc: (c) => c.time / (c.incorrect + 1),
                  defaultSortDirection: "desc",
                },
              ]}
              defaultSort={{ property: "chapter", direction: "asc" }}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <div className="page verse-cards-page">
      {phase === "GAME" ? (
        <h1 style={{ position: "fixed", left: -1000, top: -1000 }}>
          Verse Cards
        </h1>
      ) : (
        <Row>
          <Link className="secondary-btn" to="/">
            Go Back
          </Link>
          <h1 style={{ marginLeft: 20 }}>Verse Cards</h1>
        </Row>
      )}
      {getMainContent()}
      {handler.snackbar}
    </div>
  );
}
